import React, { useState, useEffect } from 'react';
import { Check, Loader2, ArrowLeft } from 'lucide-react';
import emailjs from '@emailjs/browser';
import '../styles/RegistrationForm.css';

// Mapping of ISO country codes to phone codes
const countryToPhoneCode = {
  'US': '+1', 'CA': '+1', 'GB': '+44', 'AU': '+61', 'DE': '+49',
  'FR': '+33', 'IT': '+39', 'ES': '+34', 'JP': '+81', 'CN': '+86',
  'IN': '+91', 'BR': '+55', 'RU': '+7', 'MX': '+52', 'ZA': '+27',
  'NG': '+234', 'EG': '+20', 'KE': '+254', 'ET': '+251', 'DZ': '+213',
  'MA': '+212', 'TZ': '+255', 'GH': '+233', 'SD': '+249', 'UG': '+256',
  // Add more mappings as needed
};

const RegistrationForm = ({ selectedPlan, onBack }) => {
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    confirmEmail: '',
    gender: '',
    dateOfBirth: '',
    // Contact Information
    phoneCode: '+1',
    phoneNumber: '',
    country: '',
    // Account Type
    userType: '',
    // Social Media
    spotify: '',
    instagram: '',
    youtube: '',
    otherLinks: '',
    // Additional Information
    comments: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Update form data and sync country/phone code if needed
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // If country changes, update phone code if a mapping exists
      if (name === 'country' && value in countryToPhoneCode) {
        newData.phoneCode = countryToPhoneCode[value];
      }
      // If phone code changes, try to find matching country
      else if (name === 'phoneCode') {
        const country = Object.entries(countryToPhoneCode).find(
          ([_, code]) => code === value
        );
        if (country) {
          newData.country = country[0];
        }
      }
      
      return newData;
    });
  };

  const validateForm = () => {
    // Check if emails match
    if (formData.email !== formData.confirmEmail) {
      setError('Email addresses do not match');
      return false;
    }
    
    // Add more validations as needed
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Prepare the full name
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      
      // Send email via EmailJS
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_email: formData.email,
          to_name: fullName,
          plan: selectedPlan.name,
          price: selectedPlan.price,
          user_type: formData.userType,
          username: formData.username,
          phone: formData.phoneCode + formData.phoneNumber,
          country: formData.country,
          gender: formData.gender,
          date_of_birth: formData.dateOfBirth,
          spotify: formData.spotify,
          instagram: formData.instagram,
          youtube: formData.youtube,
          other_links: formData.otherLinks,
          comments: formData.comments,
          subject: `Falphin - New ${selectedPlan.name} Application`
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      
      setIsSuccess(true);
    } catch (err) {
      console.error('Error sending email:', err);
      setError('Failed to submit the form. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="registration-success">
        <div className="success-message">
          <div className="success-icon">
            <Check size={24} />
          </div>
          <h2>Application Submitted</h2>
          <p>Thank you for your interest in Falphin! We've received your application for the <strong>{selectedPlan.name}</strong> plan.</p>
          <div className="success-actions">
            <button onClick={onBack} className="back-to-form">
              <ArrowLeft size={16} /> Back to Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-form-container">
      <div className="form-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={18} /> Back
        </button>
        <h2>Submit Your Application</h2>
        <p className="selected-plan">
          Selected Plan: <strong>{selectedPlan.name}</strong>
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-section">
          <h3>Personal Information</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Choose a username"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmEmail">Confirm Email *</label>
            <input
              type="email"
              id="confirmEmail"
              name="confirmEmail"
              value={formData.confirmEmail}
              onChange={handleChange}
              placeholder="Confirm your email"
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gender">Gender</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>Contact Information</h3>
          
          <div className="form-row">
            <div className="form-group" style={{ flex: '0 0 120px' }}>
              <label htmlFor="phoneCode">Country Code</label>
              <select
                id="phoneCode"
                name="phoneCode"
                value={formData.phoneCode}
                onChange={handleChange}
              >
                <option value="+93">+93 (AF)</option>
                <option value="+355">+355 (AL)</option>
                <option value="+213">+213 (DZ)</option>
                <option value="+1-684">+1-684 (AS)</option>
                <option value="+376">+376 (AD)</option>
                <option value="+244">+244 (AO)</option>
                <option value="+1-264">+1-264 (AI)</option>
                <option value="+672">+672 (AQ)</option>
                <option value="+1-268">+1-268 (AG)</option>
                <option value="+54">+54 (AR)</option>
                <option value="+374">+374 (AM)</option>
                <option value="+297">+297 (AW)</option>
                <option value="+61">+61 (AU)</option>
                <option value="+43">+43 (AT)</option>
                <option value="+994">+994 (AZ)</option>
                <option value="+1-242">+1-242 (BS)</option>
                <option value="+973">+973 (BH)</option>
                <option value="+880">+880 (BD)</option>
                <option value="+1-246">+1-246 (BB)</option>
                <option value="+375">+375 (BY)</option>
                <option value="+32">+32 (BE)</option>
                <option value="+501">+501 (BZ)</option>
                <option value="+229">+229 (BJ)</option>
                <option value="+1-441">+1-441 (BM)</option>
                <option value="+975">+975 (BT)</option>
                <option value="+591">+591 (BO)</option>
                <option value="+387">+387 (BA)</option>
                <option value="+267">+267 (BW)</option>
                <option value="+55">+55 (BR)</option>
                <option value="+246">+246 (IO)</option>
                <option value="+1-284">+1-284 (VG)</option>
                <option value="+673">+673 (BN)</option>
                <option value="+359">+359 (BG)</option>
                <option value="+226">+226 (BF)</option>
                <option value="+257">+257 (BI)</option>
                <option value="+855">+855 (KH)</option>
                <option value="+237">+237 (CM)</option>
                <option value="+1">+1 (CA/US)</option>
                <option value="+238">+238 (CV)</option>
                <option value="+1-345">+1-345 (KY)</option>
                <option value="+236">+236 (CF)</option>
                <option value="+235">+235 (TD)</option>
                <option value="+56">+56 (CL)</option>
                <option value="+86">+86 (CN)</option>
                <option value="+61">+61 (CX)</option>
                <option value="+61">+61 (CC)</option>
                <option value="+57">+57 (CO)</option>
                <option value="+269">+269 (KM)</option>
                <option value="+682">+682 (CK)</option>
                <option value="+506">+506 (CR)</option>
                <option value="+385">+385 (HR)</option>
                <option value="+53">+53 (CU)</option>
                <option value="+599">+599 (CW)</option>
                <option value="+357">+357 (CY)</option>
                <option value="+420">+420 (CZ)</option>
                <option value="+243">+243 (CD)</option>
                <option value="+45">+45 (DK)</option>
                <option value="+253">+253 (DJ)</option>
                <option value="+1-767">+1-767 (DM)</option>
                <option value="+1-809">+1-809 (DO)</option>
                <option value="+670">+670 (TL)</option>
                <option value="+593">+593 (EC)</option>
                <option value="+20">+20 (EG)</option>
                <option value="+503">+503 (SV)</option>
                <option value="+240">+240 (GQ)</option>
                <option value="+291">+291 (ER)</option>
                <option value="+372">+372 (EE)</option>
                <option value="+251">+251 (ET)</option>
                <option value="+500">+500 (FK)</option>
                <option value="+298">+298 (FO)</option>
                <option value="+679">+679 (FJ)</option>
                <option value="+358">+358 (FI)</option>
                <option value="+33">+33 (FR)</option>
                <option value="+689">+689 (PF)</option>
                <option value="+241">+241 (GA)</option>
                <option value="+220">+220 (GM)</option>
                <option value="+995">+995 (GE)</option>
                <option value="+49">+49 (DE)</option>
                <option value="+233">+233 (GH)</option>
                <option value="+350">+350 (GI)</option>
                <option value="+30">+30 (GR)</option>
                <option value="+299">+299 (GL)</option>
                <option value="+1-473">+1-473 (GD)</option>
                <option value="+1-671">+1-671 (GU)</option>
                <option value="+502">+502 (GT)</option>
                <option value="+44-1481">+44-1481 (GG)</option>
                <option value="+224">+224 (GN)</option>
                <option value="+245">+245 (GW)</option>
                <option value="+592">+592 (GY)</option>
                <option value="+509">+509 (HT)</option>
                <option value="+504">+504 (HN)</option>
                <option value="+852">+852 (HK)</option>
                <option value="+36">+36 (HU)</option>
                <option value="+354">+354 (IS)</option>
                <option value="+91">+91 (IN)</option>
                <option value="+62">+62 (ID)</option>
                <option value="+98">+98 (IR)</option>
                <option value="+964">+964 (IQ)</option>
                <option value="+353">+353 (IE)</option>
                <option value="+44-1624">+44-1624 (IM)</option>
                <option value="+972">+972 (IL)</option>
                <option value="+39">+39 (IT)</option>
                <option value="+225">+225 (CI)</option>
                <option value="+1-876">+1-876 (JM)</option>
                <option value="+81">+81 (JP)</option>
                <option value="+44-1534">+44-1534 (JE)</option>
                <option value="+962">+962 (JO)</option>
                <option value="+7">+7 (KZ)</option>
                <option value="+254">+254 (KE)</option>
                <option value="+686">+686 (KI)</option>
                <option value="+383">+383 (XK)</option>
                <option value="+965">+965 (KW)</option>
                <option value="+996">+996 (KG)</option>
                <option value="+856">+856 (LA)</option>
                <option value="+371">+371 (LV)</option>
                <option value="+961">+961 (LB)</option>
                <option value="+266">+266 (LS)</option>
                <option value="+231">+231 (LR)</option>
                <option value="+218">+218 (LY)</option>
                <option value="+423">+423 (LI)</option>
                <option value="+370">+370 (LT)</option>
                <option value="+352">+352 (LU)</option>
                <option value="+853">+853 (MO)</option>
                <option value="+389">+389 (MK)</option>
                <option value="+261">+261 (MG)</option>
                <option value="+265">+265 (MW)</option>
                <option value="+60">+60 (MY)</option>
                <option value="+960">+960 (MV)</option>
                <option value="+223">+223 (ML)</option>
                <option value="+356">+356 (MT)</option>
                <option value="+692">+692 (MH)</option>
                <option value="+222">+222 (MR)</option>
                <option value="+230">+230 (MU)</option>
                <option value="+262">+262 (YT)</option>
                <option value="+52">+52 (MX)</option>
                <option value="+691">+691 (FM)</option>
                <option value="+373">+373 (MD)</option>
                <option value="+377">+377 (MC)</option>
                <option value="+976">+976 (MN)</option>
                <option value="+382">+382 (ME)</option>
                <option value="+1-664">+1-664 (MS)</option>
                <option value="+212">+212 (MA)</option>
                <option value="+258">+258 (MZ)</option>
                <option value="+95">+95 (MM)</option>
                <option value="+264">+264 (NA)</option>
                <option value="+674">+674 (NR)</option>
                <option value="+977">+977 (NP)</option>
                <option value="+31">+31 (NL)</option>
                <option value="+599">+599 (AN)</option>
                <option value="+687">+687 (NC)</option>
                <option value="+64">+64 (NZ)</option>
                <option value="+505">+505 (NI)</option>
                <option value="+227">+227 (NE)</option>
                <option value="+234">+234 (NG)</option>
                <option value="+683">+683 (NU)</option>
                <option value="+850">+850 (KP)</option>
                <option value="+1-670">+1-670 (MP)</option>
                <option value="+47">+47 (NO)</option>
                <option value="+968">+968 (OM)</option>
                <option value="+92">+92 (PK)</option>
                <option value="+680">+680 (PW)</option>
                <option value="+970">+970 (PS)</option>
                <option value="+507">+507 (PA)</option>
                <option value="+675">+675 (PG)</option>
                <option value="+595">+595 (PY)</option>
                <option value="+51">+51 (PE)</option>
                <option value="+63">+63 (PH)</option>
                <option value="+64">+64 (PN)</option>
                <option value="+48">+48 (PL)</option>
                <option value="+351">+351 (PT)</option>
                <option value="+1-787">+1-787 (PR)</option>
                <option value="+974">+974 (QA)</option>
                <option value="+242">+242 (CG)</option>
                <option value="+40">+40 (RO)</option>
                <option value="+7">+7 (RU)</option>
                <option value="+250">+250 (RW)</option>
                <option value="+590">+590 (BL)</option>
                <option value="+290">+290 (SH)</option>
                <option value="+1-869">+1-869 (KN)</option>
                <option value="+1-758">+1-758 (LC)</option>
                <option value="+590">+590 (MF)</option>
                <option value="+508">+508 (PM)</option>
                <option value="+1-784">+1-784 (VC)</option>
                <option value="+685">+685 (WS)</option>
                <option value="+378">+378 (SM)</option>
                <option value="+239">+239 (ST)</option>
                <option value="+966">+966 (SA)</option>
                <option value="+221">+221 (SN)</option>
                <option value="+381">+381 (RS)</option>
                <option value="+248">+248 (SC)</option>
                <option value="+232">+232 (SL)</option>
                <option value="+65">+65 (SG)</option>
                <option value="+1-721">+1-721 (SX)</option>
                <option value="+421">+421 (SK)</option>
                <option value="+386">+386 (SI)</option>
                <option value="+677">+677 (SB)</option>
                <option value="+252">+252 (SO)</option>
                <option value="+27">+27 (ZA)</option>
                <option value="+82">+82 (KR)</option>
                <option value="+211">+211 (SS)</option>
                <option value="+34">+34 (ES)</option>
                <option value="+94">+94 (LK)</option>
                <option value="+249">+249 (SD)</option>
                <option value="+597">+597 (SR)</option>
                <option value="+47">+47 (SJ)</option>
                <option value="+268">+268 (SZ)</option>
                <option value="+46">+46 (SE)</option>
                <option value="+41">+41 (CH)</option>
                <option value="+963">+963 (SY)</option>
                <option value="+886">+886 (TW)</option>
                <option value="+992">+992 (TJ)</option>
                <option value="+255">+255 (TZ)</option>
                <option value="+66">+66 (TH)</option>
                <option value="+228">+228 (TG)</option>
                <option value="+690">+690 (TK)</option>
                <option value="+676">+676 (TO)</option>
                <option value="+1-868">+1-868 (TT)</option>
                <option value="+216">+216 (TN)</option>
                <option value="+90">+90 (TR)</option>
                <option value="+993">+993 (TM)</option>
                <option value="+1-649">+1-649 (TC)</option>
                <option value="+688">+688 (TV)</option>
                <option value="+1-340">+1-340 (VI)</option>
                <option value="+256">+256 (UG)</option>
                <option value="+380">+380 (UA)</option>
                <option value="+971">+971 (AE)</option>
                <option value="+44">+44 (GB)</option>
                <option value="+598">+598 (UY)</option>
                <option value="+998">+998 (UZ)</option>
                <option value="+678">+678 (VU)</option>
                <option value="+379">+379 (VA)</option>
                <option value="+58">+58 (VE)</option>
                <option value="+84">+84 (VN)</option>
                <option value="+681">+681 (WF)</option>
                <option value="+212">+212 (EH)</option>
                <option value="+967">+967 (YE)</option>
                <option value="+260">+260 (ZM)</option>
                <option value="+263">+263 (ZW)</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Phone number"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
            >
              <option value="">Select your country</option>
              <option value="AF">Afghanistan</option>
              <option value="AX">Åland Islands</option>
              <option value="AL">Albania</option>
              <option value="DZ">Algeria</option>
              <option value="AS">American Samoa</option>
              <option value="AD">Andorra</option>
              <option value="AO">Angola</option>
              <option value="AI">Anguilla</option>
              <option value="AQ">Antarctica</option>
              <option value="AG">Antigua & Barbuda</option>
              <option value="AR">Argentina</option>
              <option value="AM">Armenia</option>
              <option value="AW">Aruba</option>
              <option value="AU">Australia</option>
              <option value="AT">Austria</option>
              <option value="AZ">Azerbaijan</option>
              <option value="BS">Bahamas</option>
              <option value="BH">Bahrain</option>
              <option value="BD">Bangladesh</option>
              <option value="BB">Barbados</option>
              <option value="BY">Belarus</option>
              <option value="BE">Belgium</option>
              <option value="BZ">Belize</option>
              <option value="BJ">Benin</option>
              <option value="BM">Bermuda</option>
              <option value="BT">Bhutan</option>
              <option value="BO">Bolivia</option>
              <option value="BA">Bosnia & Herzegovina</option>
              <option value="BW">Botswana</option>
              <option value="BV">Bouvet Island</option>
              <option value="BR">Brazil</option>
              <option value="IO">British Indian Ocean Territory</option>
              <option value="BN">Brunei Darussalam</option>
              <option value="BG">Bulgaria</option>
              <option value="BF">Burkina Faso</option>
              <option value="BI">Burundi</option>
              <option value="KH">Cambodia</option>
              <option value="CM">Cameroon</option>
              <option value="CA">Canada</option>
              <option value="CV">Cape Verde</option>
              <option value="KY">Cayman Islands</option>
              <option value="CF">Central African Republic</option>
              <option value="TD">Chad</option>
              <option value="CL">Chile</option>
              <option value="CN">China</option>
              <option value="CX">Christmas Island</option>
              <option value="CC">Cocos (Keeling) Islands</option>
              <option value="CO">Colombia</option>
              <option value="KM">Comoros</option>
              <option value="CG">Congo</option>
              <option value="CD">Congo, Democratic Republic</option>
              <option value="CK">Cook Islands</option>
              <option value="CR">Costa Rica</option>
              <option value="CI">Côte d'Ivoire</option>
              <option value="HR">Croatia</option>
              <option value="CU">Cuba</option>
              <option value="CY">Cyprus</option>
              <option value="CZ">Czech Republic</option>
              <option value="DK">Denmark</option>
              <option value="DJ">Djibouti</option>
              <option value="DM">Dominica</option>
              <option value="DO">Dominican Republic</option>
              <option value="EC">Ecuador</option>
              <option value="EG">Egypt</option>
              <option value="SV">El Salvador</option>
              <option value="GQ">Equatorial Guinea</option>
              <option value="ER">Eritrea</option>
              <option value="EE">Estonia</option>
              <option value="ET">Ethiopia</option>
              <option value="FK">Falkland Islands</option>
              <option value="FO">Faroe Islands</option>
              <option value="FJ">Fiji</option>
              <option value="FI">Finland</option>
              <option value="FR">France</option>
              <option value="GF">French Guiana</option>
              <option value="PF">French Polynesia</option>
              <option value="TF">French Southern Territories</option>
              <option value="GA">Gabon</option>
              <option value="GM">Gambia</option>
              <option value="GE">Georgia</option>
              <option value="DE">Germany</option>
              <option value="GH">Ghana</option>
              <option value="GI">Gibraltar</option>
              <option value="GR">Greece</option>
              <option value="GL">Greenland</option>
              <option value="GD">Grenada</option>
              <option value="GP">Guadeloupe</option>
              <option value="GU">Guam</option>
              <option value="GT">Guatemala</option>
              <option value="GG">Guernsey</option>
              <option value="GN">Guinea</option>
              <option value="GW">Guinea-Bissau</option>
              <option value="GY">Guyana</option>
              <option value="HT">Haiti</option>
              <option value="HM">Heard & McDonald Islands</option>
              <option value="VA">Vatican City</option>
              <option value="HN">Honduras</option>
              <option value="HK">Hong Kong</option>
              <option value="HU">Hungary</option>
              <option value="IS">Iceland</option>
              <option value="IN">India</option>
              <option value="ID">Indonesia</option>
              <option value="IR">Iran</option>
              <option value="IQ">Iraq</option>
              <option value="IE">Ireland</option>
              <option value="IM">Isle of Man</option>
              <option value="IL">Israel</option>
              <option value="IT">Italy</option>
              <option value="JM">Jamaica</option>
              <option value="JP">Japan</option>
              <option value="JE">Jersey</option>
              <option value="JO">Jordan</option>
              <option value="KZ">Kazakhstan</option>
              <option value="KE">Kenya</option>
              <option value="KI">Kiribati</option>
              <option value="KP">North Korea</option>
              <option value="KR">South Korea</option>
              <option value="KW">Kuwait</option>
              <option value="KG">Kyrgyzstan</option>
              <option value="LA">Laos</option>
              <option value="LV">Latvia</option>
              <option value="LB">Lebanon</option>
              <option value="LS">Lesotho</option>
              <option value="LR">Liberia</option>
              <option value="LY">Libya</option>
              <option value="LI">Liechtenstein</option>
              <option value="LT">Lithuania</option>
              <option value="LU">Luxembourg</option>
              <option value="MO">Macao</option>
              <option value="MK">North Macedonia</option>
              <option value="MG">Madagascar</option>
              <option value="MW">Malawi</option>
              <option value="MY">Malaysia</option>
              <option value="MV">Maldives</option>
              <option value="ML">Mali</option>
              <option value="MT">Malta</option>
              <option value="MH">Marshall Islands</option>
              <option value="MQ">Martinique</option>
              <option value="MR">Mauritania</option>
              <option value="MU">Mauritius</option>
              <option value="YT">Mayotte</option>
              <option value="MX">Mexico</option>
              <option value="FM">Micronesia</option>
              <option value="MD">Moldova</option>
              <option value="MC">Monaco</option>
              <option value="MN">Mongolia</option>
              <option value="ME">Montenegro</option>
              <option value="MS">Montserrat</option>
              <option value="MA">Morocco</option>
              <option value="MZ">Mozambique</option>
              <option value="MM">Myanmar</option>
              <option value="NA">Namibia</option>
              <option value="NR">Nauru</option>
              <option value="NP">Nepal</option>
              <option value="NL">Netherlands</option>
              <option value="NC">New Caledonia</option>
              <option value="NZ">New Zealand</option>
              <option value="NI">Nicaragua</option>
              <option value="NE">Niger</option>
              <option value="NG">Nigeria</option>
              <option value="NU">Niue</option>
              <option value="NF">Norfolk Island</option>
              <option value="MP">Northern Mariana Islands</option>
              <option value="NO">Norway</option>
              <option value="OM">Oman</option>
              <option value="PK">Pakistan</option>
              <option value="PW">Palau</option>
              <option value="PS">Palestine</option>
              <option value="PA">Panama</option>
              <option value="PG">Papua New Guinea</option>
              <option value="PY">Paraguay</option>
              <option value="PE">Peru</option>
              <option value="PH">Philippines</option>
              <option value="PN">Pitcairn</option>
              <option value="PL">Poland</option>
              <option value="PT">Portugal</option>
              <option value="PR">Puerto Rico</option>
              <option value="QA">Qatar</option>
              <option value="RE">Réunion</option>
              <option value="RO">Romania</option>
              <option value="RU">Russia</option>
              <option value="RW">Rwanda</option>
              <option value="BL">Saint Barthélemy</option>
              <option value="SH">Saint Helena</option>
              <option value="KN">Saint Kitts and Nevis</option>
              <option value="LC">Saint Lucia</option>
              <option value="MF">Saint Martin (French part)</option>
              <option value="PM">Saint Pierre and Miquelon</option>
              <option value="VC">Saint Vincent and the Grenadines</option>
              <option value="WS">Samoa</option>
              <option value="SM">San Marino</option>
              <option value="ST">Sao Tome and Principe</option>
              <option value="SA">Saudi Arabia</option>
              <option value="SN">Senegal</option>
              <option value="RS">Serbia</option>
              <option value="SC">Seychelles</option>
              <option value="SL">Sierra Leone</option>
              <option value="SG">Singapore</option>
              <option value="SX">Sint Maarten (Dutch part)</option>
              <option value="SK">Slovakia</option>
              <option value="SI">Slovenia</option>
              <option value="SB">Solomon Islands</option>
              <option value="SO">Somalia</option>
              <option value="ZA">South Africa</option>
              <option value="GS">South Georgia & South Sandwich Islands</option>
              <option value="SS">South Sudan</option>
              <option value="ES">Spain</option>
              <option value="LK">Sri Lanka</option>
              <option value="SD">Sudan</option>
              <option value="SR">Suriname</option>
              <option value="SJ">Svalbard and Jan Mayen</option>
              <option value="SZ">Eswatini</option>
              <option value="SE">Sweden</option>
              <option value="CH">Switzerland</option>
              <option value="SY">Syria</option>
              <option value="TW">Taiwan</option>
              <option value="TJ">Tajikistan</option>
              <option value="TZ">Tanzania</option>
              <option value="TH">Thailand</option>
              <option value="TL">Timor-Leste</option>
              <option value="TG">Togo</option>
              <option value="TK">Tokelau</option>
              <option value="TO">Tonga</option>
              <option value="TT">Trinidad and Tobago</option>
              <option value="TN">Tunisia</option>
              <option value="TR">Turkey</option>
              <option value="TM">Turkmenistan</option>
              <option value="TC">Turks and Caicos Islands</option>
              <option value="TV">Tuvalu</option>
              <option value="UG">Uganda</option>
              <option value="UA">Ukraine</option>
              <option value="AE">United Arab Emirates</option>
              <option value="GB">United Kingdom</option>
              <option value="US">United States</option>
              <option value="UM">U.S. Outlying Islands</option>
              <option value="UY">Uruguay</option>
              <option value="UZ">Uzbekistan</option>
              <option value="VU">Vanuatu</option>
              <option value="VE">Venezuela</option>
              <option value="VN">Vietnam</option>
              <option value="VG">British Virgin Islands</option>
              <option value="VI">U.S. Virgin Islands</option>
              <option value="WF">Wallis and Futuna</option>
              <option value="EH">Western Sahara</option>
              <option value="YE">Yemen</option>
              <option value="ZM">Zambia</option>
              <option value="ZW">Zimbabwe</option>
            </select>
          </div>
        </div>
        
        <div className="form-section">
          <h3>Account Type</h3>
          <div className="form-group">
            <label htmlFor="userType">I am a *</label>
            <select
              id="userType"
              name="userType"
              value={formData.userType}
              onChange={handleChange}
              required
            >
              <option value="">Select your role</option>
              <option value="artist">Solo Artist</option>
              <option value="band">Band</option>
              <option value="producer">Producer</option>
              <option value="label">Record Label</option>
            </select>
          </div>
        </div>
        
        <div className="form-section">
          <h3>Your Music & Socials</h3>
          <p className="section-description">Help us verify your identity and promote your music better</p>
          
          <div className="form-group">
            <label htmlFor="spotify">Spotify Artist Link</label>
            <input
              type="url"
              id="spotify"
              name="spotify"
              placeholder="https://open.spotify.com/artist/..."
              value={formData.spotify}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="instagram">Instagram</label>
            <div className="input-with-prefix">
              <span className="prefix">instagram.com/</span>
              <input
                type="text"
                id="instagram"
                name="instagram"
                placeholder="yourusername"
                value={formData.instagram}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="youtube">YouTube Channel</label>
            <input
              type="url"
              id="youtube"
              name="youtube"
              placeholder="https://youtube.com/..."
              value={formData.youtube}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="otherLinks">Other Links (SoundCloud, Apple Music, etc.)</label>
            <textarea
              id="otherLinks"
              name="otherLinks"
              placeholder="Paste your links here, one per line"
              value={formData.otherLinks}
              onChange={handleChange}
              rows="3"
            />
          </div>
        </div>
        
        <div className="form-footer">
          <p className="privacy-notice">
            By submitting this form, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>.
          </p>
          
          {error && <div className="error-message">{error}</div>}
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="spinner" size={18} />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;